import { useEffect, useState } from "react";
import { tagular } from "@cohesion/tagular";
import PropTypes from "prop-types";
import cn from "classnames";

import {
  validateLegacyAPI,
  validatePoiPageAPI,
  poiDetailAPI,
} from "@queries/pois/detail/api";

import { Ad } from "@components/ad";
import { Breadcrumbs } from "@components/breadcrumbs";
import { Button } from "@components/button";
import { Hero } from "@components/heroGallery";
import { NextHead } from "@components/nextHead";
import { SuggestAnEdit } from "@components/suggestAnEdit/suggestAnEdit";
import { Carousel } from "@components/carousel";
import { PoiCard } from "@components/cards/poi";
import { Pageblock } from "@components/pageblock";
import { withSave } from "@components/_hocs/withSave";
import { SaveButton } from "@components/saveButton";

import { Globe, MapPin, Phone, TrainFill } from "@icons";
import { UltimateGuide } from "@icons/badges";

import { Body } from "@pageComponents/articles/body";

import { LP_URL } from "@constants/lpUrl";

import { airbrakeNode } from "@utils/airbrake/node";
import { keyify } from "@utils/keyify";
import { addBreaksToUrl } from "@utils/url/addBreaksToUrl";
import { ensureProtocol } from "@utils/url/ensureProtocol";
import { useIntersectionObserver } from "@utils/useIntersectionObserver";
import { dangerHTML } from "@utils/danger";

const SavePoiCard = withSave(PoiCard);
const SavePoiCardNearby = withSave(PoiCard, "bg-white rounded");

export default function POIDetail({
  breadcrumb,
  contactPracticalities,
  editorial,
  esid,
  featuredImage = null,
  feedbackCtx,
  gygTours = null,
  head,
  images = null,
  meta,
  mustSeePois = null,
  nearbyPois = null,
  places,
  slug,
  title,
}) {
  const [isLoadMore, setIsLoadMore] = useState(false);
  const { setNodeRef, nodeRef, isVisible } = useIntersectionObserver(
    {
      threshold: 0.5,
    },
    false
  );

  // GetYourGuide tracking
  useEffect(() => {
    const iframeMatch = nodeRef?.querySelectorAll("iframe")?.length > 0;
    if (isVisible && iframeMatch) {
      tagular("view", {
        actionOutcome: "EXTERNALLINK",
        webElement: {
          location: "SECTION",
          elementType: "LINK",
          position: "GETYOURGUIDE",
          text: Array.isArray(gygTours) ? `tours-${gygTours.join(",")}` : "",
          name:
            Array.isArray(gygTours) && gygTours[0]
              ? "AVAILABILITYWIDGET"
              : "AVAILABILITYWIDGET-FALLBACK",
        },
      });
    }
  }, [isVisible, nodeRef, gygTours]);

  const loadMore = () => {
    setIsLoadMore(true);
    tagular("click", {
      actionOutcome: "INTERNALLINK",
      outboundUrl: "NULL",
      webElement: {
        location: "SECTION",
        elementType: "BUTTON",
        position: "NEARBY",
        text: "LOADMORE",
      },
    });
  };

  return (
    <>
      <NextHead>
        {["Sleep", "Shop", "Drink", "Eat", "Entertainment"].includes(
          meta.type.title
        ) && <meta key="googlebot" name="googlebot" content="noindex" />}

        <title>{head.title}</title>
        <meta key="title" name="title" content={head.title} />
        <meta key="twitter:title" name="twitter:title" content={head.title} />
        <meta key="og:title" property="og:title" content={head.title} />
        <meta key="description" name="description" content={head.description} />
        <meta
          key="og:description"
          property="og:description"
          content={head.description}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={head.description}
        />
        <link key="canonical" rel="canonical" href={head.url} />
        <meta key="twitter:url" name="twitter:url" content={head.url} />
        <meta key="og:url" property="og:url" content={head.url} />
        {head.image && (
          <meta key="og:image" property="og:image" content={head.image} />
        )}
        {head.image && (
          <meta key="twitter:image" name="twitter:image" content={head.image} />
        )}
        {head.schema && (
          <script type="application/ld+json" {...dangerHTML(head.schema)} />
        )}
      </NextHead>

      <Ad
        placement="superzone"
        path="Destinations/POIs/hero"
        additionalTargeting={{
          position: "hero",
        }}
        className="max-w-full mb-8 overflow-x-hidden"
      />

      {featuredImage && <Hero feature={featuredImage} secondary={images} />}

      <div className="container">
        <header className="items-end justify-between my-8 lg:flex lg:mt-12 lg:mb-20">
          <div className="lg:w-3/4">
            <h1 className="inline text-3xl font-semibold lg:text-6xl text-blue">
              {title}{" "}
            </h1>
            <div className="inline text-xl">
              <Breadcrumbs useComma inline breadcrumbs={breadcrumb} />
            </div>
            {(meta.isTopChoice || places[0]) && (
              <div className="italic">
                <p>
                  {meta.isTopChoice && (
                    <span className="font-bold text-red">Top choice</span>
                  )}
                  {places[0] &&
                    `${meta.isTopChoice ? " in " : ""}${places[0].title}`}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-6 mt-4 lg:mt-0 wrap">
            <SaveButton esid={esid} type="poi" slug={slug} />
          </div>
        </header>

        <hr className="container text-[#DBDBDB]" />

        <div className="max-w-4xl mx-auto my-10">
          {meta.isUltimateGuide && (
            <article className="grid items-center grid-cols-6 gap-4 lg:gap-x-6">
              <div className="col-span-2 text-center sm:col-span-1">
                <UltimateGuide
                  className="w-full h-auto"
                  height="143"
                  width="143"
                />
              </div>
              <h3 className="col-span-4 text-2xl sm:col-span-5 lg:text-5xl">
                Lonely Planet&apos;s Ultimate Guide
              </h3>
              <p className="col-span-6 text-sm lg:col-start-2 lg:col-end-8 lg:text-lg">
                Explore insider tips, fascinating history and surprising secrets
                to make the most of your experience.
              </p>
            </article>
          )}
          <article className="my-8 lg:my-16">
            <Body html={editorial} withReadMore />
          </article>

          {Array.isArray(gygTours) && gygTours[0] ? (
            <div className="mb-8 lg:mb-16" data-gyg-wrapper ref={setNodeRef}>
              <Carousel
                pageSize={1}
                scrollPadding={false}
                tagularPosition="GETYOURGUIDE"
                tagularName="AVAILABILITYWIDGET"
              >
                {gygTours.map(tourID => (
                  <div
                    key={tourID}
                    data-gyg-tour-id={tourID}
                    data-gyg-href="https://widget.getyourguide.com/default/availability.frame"
                    data-gyg-locale-code="en-US"
                    data-gyg-currency="USD"
                    data-gyg-widget="availability"
                    data-gyg-variant="horizontal"
                    data-gyg-partner-id="MH9NGR8"
                    data-gyg-cmp="poi-detail"
                  />
                ))}
              </Carousel>
            </div>
          ) : (
            <div
              className="mb-8 lg:mb-16"
              ref={setNodeRef}
              data-gyg-href="https://widget.getyourguide.com/default/availability.frame"
              data-gyg-locale-code="en-US"
              data-gyg-currency="USD"
              data-gyg-widget="availability"
              data-gyg-variant="horizontal"
              data-gyg-partner-id="MH9NGR8"
              data-gyg-cmp="poi-detail-fallback"
            />
          )}

          <Ad
            placement="superzone"
            path="Destinations/POIs/middle"
            additionalTargeting={{
              position: "middle",
            }}
            className="max-w-full my-8 overflow-x-hidden lg:my-16"
          />

          {/** TODO: move transport section above body when more "Plan your Visit" content has been added */}
          {process.env.NEXT_PUBLIC_DOTCOM_FRONTEND_ENV !== "production" &&
            Array.isArray(contactPracticalities.transport) &&
            contactPracticalities.transport[0] && (
              <div className="px-4 py-6 my-8 bg-red-100 rounded lg:px-10 lg:my16">
                <h2 className="flex items-center heading-04 lg:mb-6">
                  <TrainFill className="h-6 mr-2 lg:h-8" /> Transportation
                </h2>
                <ul>
                  {contactPracticalities.transport.map(item => (
                    <li key={keyify(item.description)}>
                      <strong className="capitalize">{item.type}</strong>:{" "}
                      {item.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          <hr className="container text-[#DBDBDB]" />

          <article className="my-8 lg:my-16">
            <div className="lg:flex">
              {(contactPracticalities.address ||
                contactPracticalities.phone ||
                contactPracticalities.website) && (
                <h3 className="mb-6 text-sm font-semibold uppercase lg:hidden">
                  Contact
                </h3>
              )}
              {contactPracticalities.address && (
                <div className="lg:mb-10 lg:w-1/3">
                  <h3 className="hidden mb-6 text-sm font-semibold uppercase lg:block">
                    Address
                  </h3>
                  <div className="flex">
                    <MapPin
                      className="flex-shrink-0 mt-1 mr-2 lg:hidden"
                      height="16"
                      width="16"
                    />
                    <p className="mb-4 text-md lg:text-lg lg:mb-0">
                      <a
                        onClick={() => {
                          tagular("click", {
                            actionOutcome: "EXTERNALLINK",
                            outboundUrl: `https://www.google.com/maps?q=${encodeURI(
                              contactPracticalities.address
                            )}`,
                            webElement: {
                              location: "SECTION",
                              position: "CONTACT",
                              text: "ADDRESS",
                              elementType: "LINK",
                            },
                          });
                        }}
                        href={`https://www.google.com/maps?q=${encodeURI(
                          contactPracticalities.address
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-link"
                      >
                        {contactPracticalities.address}
                      </a>
                    </p>
                  </div>
                </div>
              )}
              {(contactPracticalities.phone ||
                contactPracticalities.website) && (
                <div className="mb-10 lg:w-2/3">
                  <h3 className="hidden mb-6 text-sm font-semibold uppercase lg:block">
                    Get In Touch
                  </h3>
                  {contactPracticalities.phone && (
                    <div className="flex">
                      <Phone
                        className="flex-shrink-0 mt-1 mr-2 lg:hidden"
                        height="16"
                        width="16"
                      />
                      <p className="mb-4 text-md lg:text-lg lg:mb-0">
                        <a
                          href={`tel:${contactPracticalities.phone}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-link"
                          onClick={() => {
                            tagular("click", {
                              actionOutcome: "EXTERNALLINK",
                              outboundUrl: `tel:${contactPracticalities.phone}`,
                              webElement: {
                                location: "SECTION",
                                position: "CONTACT",
                                text: "PHONE",
                                elementType: "LINK",
                              },
                            });
                          }}
                        >
                          {contactPracticalities.phone}
                        </a>
                      </p>
                    </div>
                  )}
                  {contactPracticalities.website && (
                    <div className="flex flex-col">
                      {contactPracticalities.website
                        .replace(/\s/g, "")
                        .split(",")
                        .map(website => (
                          <div className="flex" key={website}>
                            <Globe
                              className="lg:hidden mt-1.5 mr-2 flex-shrink-0"
                              height="16"
                              width="16"
                            />
                            <p className="mb-4 text-md lg:text-lg lg:mb-0">
                              <a
                                href={ensureProtocol(website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-link"
                                onClick={() => {
                                  tagular("click", {
                                    actionOutcome: "EXTERNALLINK",
                                    outboundUrl: ensureProtocol(website),
                                    webElement: {
                                      location: "SECTION",
                                      position: "CONTACT",
                                      text: "WEBSITE",
                                      elementType: "LINK",
                                    },
                                  });
                                }}
                              >
                                {addBreaksToUrl(ensureProtocol(website))}
                              </a>
                            </p>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <SuggestAnEdit className="my-8" customFields={feedbackCtx} />
          </article>
        </div>
      </div>

      {(mustSeePois || nearbyPois) && (
        <div className="lg:p-6">
          <Pageblock
            inset
            className="w-full bg-black-100 md:p-10 lg:rounded-lg"
          >
            {mustSeePois && (
              <>
                <div className="container">
                  <h2 className="mb-16 text-2xl text-center lg:text-5xl">
                    Lonely Planet&apos;s must-see attractions
                  </h2>
                </div>
                <Carousel
                  pageSize={4}
                  contained={false}
                  scrollPadding={false}
                  tagularPosition="MUSTSEE"
                  tagularName="MUSTSEE"
                  link={{
                    href: places ? `/${places[0]?.slug}/attractions` : null,
                    text: places ? "View more attractions" : null,
                  }}
                >
                  {mustSeePois.map(poi => (
                    <SavePoiCard
                      key={poi.esid}
                      tagularPosition="MUSTSEE"
                      hocTagularCtx={{ name: "MUSTSEE", position: "POI" }}
                      category="attractions"
                      {...poi}
                    />
                  ))}
                </Carousel>
              </>
            )}
            {nearbyPois && (
              <div className={cn("container md:p-0", mustSeePois && "mt-32")}>
                <h2 className="mb-16 text-2xl text-center lg:text-5xl">
                  Nearby {places && places[0]?.title} attractions
                </h2>
                <div
                  className={cn(
                    "max-h-[690px] overflow-hidden",
                    isLoadMore && "max-h-none"
                  )}
                >
                  <div className="grid grid-cols-1 gap-4 lg:container md:grid-cols-4 auto-rows-fr">
                    {nearbyPois.map((poi, key) => (
                      <SavePoiCardNearby
                        tagularPosition="NEARBY"
                        tagularName="NEARBY"
                        hocTagularCtx={{ name: "NEARBY", position: "POI" }}
                        className="test"
                        key={poi.esid}
                        count={key + 1}
                        {...poi}
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-8 text-center md:mt-16">
                  <Button
                    onClick={() => loadMore()}
                    className={cn(
                      "md:hidden",
                      isLoadMore ? "hidden" : "md:inline-block"
                    )}
                    type="button"
                  >
                    View more
                  </Button>
                  {places && (
                    <Button
                      className={
                        isLoadMore ? "inline-block" : "hidden md:inline-block"
                      }
                      href={places && `/${places[0]?.slug}/attractions`}
                      onClick={() => {
                        tagular("click", {
                          actionOutcome: "INTERNALLINK",
                          outboundUrl: `/${places[0].slug}/attractions`,
                          webElement: {
                            location: "SECTION",
                            position: "NEARBY",
                            text: "VIEWMORE",
                            elementType: "BUTTON",
                          },
                        });
                      }}
                    >
                      View more attractions
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Pageblock>
        </div>
      )}

      <Ad
        placement="superzone"
        path="Destinations/POIs/superzone"
        additionalTargeting={{
          position: "superzone",
        }}
        className="max-w-full my-8 overflow-x-hidden"
      />
    </>
  );
}

export async function getServerSideProps({ req, query, res }) {
  res.setHeader("Cache-Control", "no-cache, max-age=3600, must-revalidate");

  const { poiId, poiRedirectId = null } = query;
  const { pathname } = new URL(`${LP_URL}${req.url}`);

  if (poiRedirectId) {
    const { isValid, redirect } = await validateLegacyAPI(poiRedirectId);

    if (!isValid) {
      return {
        notFound: true,
      };
    }

    if (redirect) {
      return {
        redirect: {
          destination: redirect,
          permanent: true,
        },
      };
    }
  }

  const { isValid, redirect } = await validatePoiPageAPI(pathname.substring(1));

  if (redirect) {
    return {
      redirect: {
        destination: redirect,
        permanent: true,
      },
    };
  }

  if (!isValid && poiId) {
    const { isValid: isValidLegacy, redirect: redirectLegacy } =
      await validateLegacyAPI(poiId);

    if (!isValidLegacy) {
      return {
        notFound: true,
      };
    }

    if (redirectLegacy) {
      return {
        redirect: {
          destination: redirectLegacy,
          permanent: true,
        },
      };
    }
  }

  if (!isValid) {
    return {
      notFound: true,
    };
  }

  const data = await poiDetailAPI({
    path: pathname.substring(1),
  });

  const { data: gygData, errors: gygErrors } = !data.title
    ? { data: null, errors: true }
    : await fetch(
        `https://api.getyourguide.com/1/tours?cnt_language=en&currency=USD&q=${data.title}`,
        {
          headers: {
            "X-Access-Token": process.env.GET_YOUR_GUIDE_API_KEY,
          },
        }
      )
        .then(async gygRes => {
          const contentType = gygRes.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            return gygRes.json();
          }
          // eslint-disable-next-line no-console
          console.warn("GYG WARNING", {
            status: gygRes.status,
            statusText: gygRes.statusText,
          });
          return { data: null, errors: true };
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error("GYG ERROR", err);
          airbrakeNode.notify({
            error: err,
            context: {
              component:
                "pages/pois/detail/index.page.js#getServerSideProps/GYG",
            },
          });
          return { data: null, errors: true };
        });

  return {
    props: {
      gygTours: gygErrors ? null : gygData?.tours.map(tour => tour.tour_id),
      ...data,
    },
  };
}

POIDetail.propTypes = {
  esid: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    priceString: PropTypes.string,
    type: PropTypes.shape({
      title: PropTypes.string,
    }),
    isTopChoice: PropTypes.bool,
    isUltimateGuide: PropTypes.bool,
  }).isRequired,
  head: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.string,
    schema: PropTypes.string,
  }).isRequired,
  title: PropTypes.string.isRequired,
  places: PropTypes.arrayOf(
    PropTypes.shape({
      meta: PropTypes.shape({}),
      slug: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
  breadcrumb: PropTypes.arrayOf(
    PropTypes.shape({ slug: PropTypes.string, title: PropTypes.string })
  ).isRequired,
  featuredImage: PropTypes.shape({
    alt: PropTypes.string,
    credit: PropTypes.string,
    url: PropTypes.string,
  }),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string,
      caption: PropTypes.string,
      credit: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  editorial: PropTypes.string.isRequired,
  feedbackCtx: PropTypes.shape({}).isRequired,
  contactPracticalities: PropTypes.shape({
    address: PropTypes.string,
    phone: PropTypes.string,
    website: PropTypes.string,
    transport: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        type: PropTypes.string,
      })
    ),
  }).isRequired,
  mustSeePois: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        topChoice: PropTypes.bool,
        href: PropTypes.string.isRequired,
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        }),
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        miles: PropTypes.number,
        cost: PropTypes.string,
      })
    ),
    PropTypes.bool,
  ]),
  nearbyPois: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        topChoice: PropTypes.bool,
        href: PropTypes.string.isRequired,
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        }),
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        miles: PropTypes.number,
        cost: PropTypes.string,
      })
    ),
    PropTypes.bool,
  ]),
  gygTours: PropTypes.arrayOf(PropTypes.number),
};
